import { useMemo } from "react";
import type { UserRow, FunnelStep } from "./sql";
import { METRIC_OPTIONS } from "./sql";
import { daysBetween, monthLabel, fmtN } from "../../lib/funnel/helpers";
import { PRIMARY, STEEL, FG1, MUTED } from "../../lib/funnel/theme";
import { SectionCardHeader } from "../../components/ChartCard";
import type { FilterPillSpec } from "../../components/FilterPills";

export type PathFilter = "all" | "demo" | "self";

export type WeekKey = "w1" | "w2" | "w4" | "w8" | "w12" | "now";

// One entry per signed-up user (post collapse-to-distinct). Every entry has a
// user — Phase 2 dropped the demo-attendee branch that previously produced
// user=null entries for attendees who never signed up.
export type SignupCohortEntry = {
  cohortDate: string;
  signupDate: string;
  isDemoPath: boolean;
  user: UserRow;
};

export const WEEK_BUCKETS: { key: WeekKey; label: string; cap: number | null }[] = [
  { key: "w1", label: "Wk 1", cap: 1 },
  { key: "w2", label: "Wk 2", cap: 2 },
  { key: "w4", label: "Wk 4", cap: 4 },
  { key: "w8", label: "Wk 8", cap: 8 },
  { key: "w12", label: "Wk 12", cap: 12 },
  { key: "now", label: "Now", cap: null },
];

const PRIMARY_RGB = "47, 122, 138"; // #2F7A8A

function intensityBg(pct: number): string {
  if (pct <= 0) return "transparent";
  const alpha = 0.12 + Math.min(1, pct / 100) * 0.72;
  return `rgba(${PRIMARY_RGB}, ${alpha})`;
}

function textOn(pct: number): string {
  return pct >= 50 ? "#fff" : "var(--fg-1)";
}

export function CohortGrid({
  signupCohort,
  metric,
  onMetricChange,
  pathFilter,
  onPathFilterChange,
  selectedWeek,
  onSelectWeek,
  maxCohorts = 12,
  pagePills,
}: {
  signupCohort: SignupCohortEntry[];
  metric: FunnelStep;
  onMetricChange: (key: string) => void;
  pathFilter: PathFilter;
  onPathFilterChange: (p: PathFilter) => void;
  selectedWeek: WeekKey;
  onSelectWeek: (k: WeekKey) => void;
  maxCohorts?: number;
  pagePills?: FilterPillSpec[];
}) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Every metric is evaluated against signupCohort entries, bucketed by
  // cohortDate (first_demo_date for demo attendees, signup date for self).
  // Demo attendees who never signed up sit in the denominator and never hit
  // the metric, so demo-path % matches the butterfly chart.
  const cohorts = useMemo(() => {
    const filtered = signupCohort.filter((e) =>
      pathFilter === "demo" ? e.isDemoPath
      : pathFilter === "self" ? !e.isDemoPath
      : true,
    );
    const grouped = new Map<string, SignupCohortEntry[]>();
    for (const e of filtered) {
      const m = (e.cohortDate ?? "").slice(0, 7);
      if (!m) continue;
      const arr = grouped.get(m) ?? [];
      arr.push(e);
      grouped.set(m, arr);
    }
    const allMonths = Array.from(grouped.keys()).sort();
    return allMonths.map((m) => {
      const entries = grouped.get(m) ?? [];
      return {
        month: m,
        N: entries.length,
        hits: (cap: number | null) => entries.filter((e) => {
          if (!metric.predicate(e.user)) return false;
          if (cap == null) return true;
          const first = metric.firstDateOf(e.user);
          if (!first) return false;
          return daysBetween(e.cohortDate, first) <= cap * 7;
        }).length,
      };
    });
  }, [signupCohort, pathFilter, metric]);

  const visibleCohorts = useMemo(() => cohorts.slice(-maxCohorts), [cohorts, maxCohorts]);

  const cells = useMemo(() => {
    return visibleCohorts.map(({ month, N, hits }) => {
      const weekRows = WEEK_BUCKETS.map((wb) => {
        const weekEndIso = wb.cap == null
          ? today
          : new Date(new Date(month + "-01T00:00:00Z").getTime() + wb.cap * 7 * 86400000)
              .toISOString().slice(0, 10);
        const isFuture = weekEndIso > today && wb.cap != null;
        if (isFuture) return { ...wb, n: 0, pct: 0, blank: true };
        const n = hits(wb.cap);
        const pct = N === 0 ? 0 : (n / N) * 100;
        return { ...wb, n, pct, blank: false };
      });
      return { month, N, weekRows };
    });
  }, [visibleCohorts, today]);

  // All-time totals: pool n and N across every cohort where the bucket has
  // matured (non-future). Lets the bottom row read like the headline KPIs.
  const totals = useMemo(() => {
    const totalN = cohorts.reduce((s, c) => s + c.N, 0);
    const weekRows = WEEK_BUCKETS.map((wb) => {
      let n = 0;
      let denom = 0;
      for (const c of cohorts) {
        const weekEndIso = wb.cap == null
          ? today
          : new Date(new Date(c.month + "-01T00:00:00Z").getTime() + wb.cap * 7 * 86400000)
              .toISOString().slice(0, 10);
        if (weekEndIso > today && wb.cap != null) continue;
        n += c.hits(wb.cap);
        denom += c.N;
      }
      const pct = denom === 0 ? 0 : (n / denom) * 100;
      return { ...wb, n, denom, pct };
    });
    return { totalN, weekRows };
  }, [cohorts, today]);

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  pills.push({ kind: "mode", label: `Metric: ${metric.label}` });
  if (pathFilter !== "all") {
    pills.push({ kind: "mode", label: pathFilter === "demo" ? "Path: demo only" : "Path: self-serve only" });
  }

  const headerExtra = (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <label style={{ fontSize: 11, color: FG1 }}>Metric</label>
      <select
        value={metric.key}
        onChange={(e) => onMetricChange(e.target.value)}
        style={{
          padding: "3px 8px",
          fontSize: 11,
          border: `1px solid ${MUTED}`,
          borderRadius: 6,
          background: "#fff",
          color: FG1,
        }}
      >
        {METRIC_OPTIONS.map((s) => (
          <option key={s.key} value={s.key}>{s.label}</option>
        ))}
      </select>
      <div role="group" aria-label="Path filter" style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 11 }}>
        {([
          { key: "all", label: "All" },
          { key: "demo", label: "Demo" },
          { key: "self", label: "Self" },
        ] as const).map((opt) => {
          const active = pathFilter === opt.key;
          const bg = active ? (opt.key === "self" ? STEEL : PRIMARY) : "transparent";
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onPathFilterChange(opt.key)}
              style={{
                padding: "3px 12px",
                background: bg,
                color: active ? "#fff" : FG1,
                border: "none",
                cursor: "pointer",
                fontWeight: active ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  if (cohorts.length === 0) {
    return (
      <section className="card">
        <SectionCardHeader
          title="Cohort conversion grid (cumulative)"
          filterPills={pills}
          headerExtra={headerExtra}
        />
        <div className="card-body"><p className="muted">No cohort data.</p></div>
      </section>
    );
  }

  return (
    <section className="card">
      <SectionCardHeader
        title="Cohort conversion grid (cumulative)"
        subtitle={
          <>Rows = weeks since signup. Columns = cohort month. Cell = % of cohort who hit &ldquo;{metric.label}&rdquo; by that week. Click a row to drive the line chart below.</>
        }
        filterPills={pills}
        headerExtra={headerExtra}
      />
      <div className="card-body">
        <div className="table-wrap">
          <table className="t" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 80, textAlign: "left" }}>Week</th>
                {cells.map((c) => (
                  <th key={c.month} style={{ textAlign: "center" }}>
                    <div>{monthLabel(c.month)}</div>
                    <div className="muted" style={{ fontSize: 10, fontWeight: 400 }}>n={fmtN(c.N)}</div>
                  </th>
                ))}
                <th style={{ textAlign: "center", borderLeft: `2px solid ${MUTED}` }}>
                  <div>All time</div>
                  <div className="muted" style={{ fontSize: 10, fontWeight: 400 }}>n={fmtN(totals.totalN)}</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {WEEK_BUCKETS.map((wb, rowIdx) => {
                const isSelected = wb.key === selectedWeek;
                return (
                  <tr
                    key={wb.key}
                    onClick={() => onSelectWeek(wb.key)}
                    style={{
                      cursor: "pointer",
                      outline: isSelected ? `2px solid ${PRIMARY}` : "none",
                      outlineOffset: -2,
                    }}
                  >
                    <td style={{ fontWeight: isSelected ? 700 : 600 }}>{wb.label}</td>
                    {cells.map((c) => {
                      const cell = c.weekRows[rowIdx];
                      if (!cell || cell.blank) {
                        return <td key={c.month} style={{ textAlign: "center", color: "var(--fg-3)" }}>—</td>;
                      }
                      return (
                        <td
                          key={c.month}
                          title={`${cell.n}/${c.N} (${cell.pct.toFixed(1)}%)`}
                          style={{
                            textAlign: "center",
                            background: intensityBg(cell.pct),
                            color: textOn(cell.pct),
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {cell.pct === 0 ? "0%" : `${cell.pct.toFixed(0)}%`}
                        </td>
                      );
                    })}
                    {(() => {
                      const t = totals.weekRows[rowIdx];
                      if (!t || t.denom === 0) {
                        return <td style={{ textAlign: "center", color: "var(--fg-3)", borderLeft: `2px solid ${MUTED}` }}>—</td>;
                      }
                      return (
                        <td
                          title={`${t.n}/${t.denom} (${t.pct.toFixed(1)}%)`}
                          style={{
                            textAlign: "center",
                            background: intensityBg(t.pct),
                            color: textOn(t.pct),
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: 600,
                            borderLeft: `2px solid ${MUTED}`,
                          }}
                        >
                          {t.pct === 0 ? "0%" : `${t.pct.toFixed(0)}%`}
                        </td>
                      );
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
